import { useUser } from 'contexts/UserContext'
import InjuredFormDetail from 'forms/Incident/Injured/InjuredFormDetail'
import useFetch from 'hooks/useFetch'
import { useToast } from 'hooks/useToast'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { ImSpinner9 } from 'react-icons/im'
import { LuMoveLeft, LuPrinter } from 'react-icons/lu'
import { useReactToPrint } from 'react-to-print'
import { useLocation } from 'wouter'

export default function InjuredViewDetail({ params }) {
  const { showToast } = useToast()
  const { user } = useUser()
  const [, setLocation] = useLocation()

  // useFetch(): hook personalizado para realizar solicitudes GET, POST, PUT, DELETE
  const [fetchedIncident, fetchIncident] = useFetch()
  const [updatedIncident, updateIncident] = useFetch()
  const [deletedIncident, deleteIncident] = useFetch()

  // Variables:
  const isNew = params?.id === 'new'
  const [fields, setFields] = useState({
    id: 0,
    status: '',
    incident_number: 0,
    agency_reference: 0,
    tus_reference: '',
    incident_risk: '',
    occurrence_date: '',
    occurrence_time: '',
    coordinates: '',
    driver: '',
    payments: '',
    description: '',
    observations: '',
    type: 'injured'
  })

  // Información necesária al usar 'react-to-print':
  const incidentRef = useRef()
  const mapRef = useRef()
  const handlePrint = useReactToPrint({
    contentRef: incidentRef,
    documentTitle: `Siniestro_TUS_Lesionados_${fields.incident_number}`
  })

  // Formatear fecha
  const formatDate = (dateString) => {
    if (
      typeof dateString === 'string' &&
      dateString !== '' &&
      dateString !== null &&
      dateString !== undefined
    ) {
      const [day, month, year] = dateString?.split('/')
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
    }

    return dateString
  }

  // CRUD: GET
  useEffect(() => {
    if (!isNew) {
      const apiCallGet = {
        url: `incidents/injured/${params.id}`,
        method: 'GET',
        messageKo: '¡Error al recoger Usuario!'
      }
      fetchIncident(apiCallGet)
    }
  }, [])

  // Escuchar GET
  useEffect(() => {
    if (fetchedIncident.data) {
      if (fetchedIncident.data?.type !== 'injured') {
        setLocation('/page-not-found')
      } else {
        setFields({
          id: fetchedIncident.data.id,
          status: fetchedIncident.data.status,
          incident_number: fetchedIncident.data.incident_number,
          agency_reference: fetchedIncident.data.agency_reference,
          tus_reference: fetchedIncident.data.tus_reference,
          incident_risk: fetchedIncident.data.incident_risk,
          occurrence_date: fetchedIncident.data.occurrence_date
            ? formatDate(fetchedIncident.data.occurrence_date)
            : '',
          occurrence_time:
            fetchedIncident.data.occurrence_time !== '' &&
            fetchedIncident.data.occurrence_time !== null &&
            fetchedIncident.data.occurrence_time !== undefined
              ? fetchedIncident.data.occurrence_time.slice(0, 5)
              : fetchedIncident.data.occurrence_time,
          coordinates: fetchedIncident.data.coordinates,
          driver: fetchedIncident.data.driver,
          payments: fetchedIncident.data.payments,
          description: fetchedIncident.data.description,
          observations: fetchedIncident.data.observations,
          type: fetchedIncident.data.type
        })
      }
    }

    if (fetchedIncident.error) {
      setLocation('/page-not-found')
    }
  }, [fetchedIncident.data, fetchedIncident.error])

  // CRUD: POST / PUT
  const handleSubmit = async (values) => {
    let url = `incidents/injured/${user.id}`
    let method = 'POST'

    const formattedValues = { ...values }

    // Si no es un nuevo usuario, actualizar datos con PUT
    if (!isNew) {
      method = 'PUT'
      url += `/${params.id}`
    } else {
      url += `/single`
    }

    delete formattedValues.id

    // F. Ocurrencia
    if (
      typeof formattedValues?.occurrence_date === 'string' &&
      formattedValues?.occurrence_date !== '' &&
      formattedValues?.occurrence_date !== null &&
      formattedValues?.occurrence_date !== undefined
    ) {
      const [year, month, day] = formattedValues?.occurrence_date?.split('-')
      formattedValues.occurrence_date = `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`
    }

    // Coordenadas
    if (
      (typeof formattedValues?.coordinates === 'string' &&
        formattedValues?.coordinates?.trim() === '') ||
      formattedValues?.coordinates === undefined
    ) {
      formattedValues.coordinates = null
    }

    const apiCallPost = {
      url,
      method,
      body: formattedValues
    }
    await updateIncident(apiCallPost)
  }

  // Mostrar mensaje API: POST / PUT (notificación de éxito o error)
  useEffect(() => {
    if (updatedIncident.isCalled) {
      if (updatedIncident.message) {
        showToast(updatedIncident.message, 'success')
        setLocation('/tus-incidents/injured/grid')
      }

      if (updatedIncident.error) {
        showToast(updatedIncident.error, 'error')
      }
    }
  }, [updatedIncident.loading])

  // CRUD: DELETE
  const handleDelete = async (id) => {
    const apiCallDelete = {
      url: `incidents/injured/${user.id}/${id}`,
      method: 'DELETE'
    }
    await deleteIncident(apiCallDelete)
  }

  // Mostrar mensaje API: DELETE (notificación de éxito o error)
  useEffect(() => {
    if (deletedIncident.isCalled) {
      if (deletedIncident.message) {
        showToast(deletedIncident.message, 'success')
        setLocation('/tus-incidents/injured/grid')
      }

      if (deletedIncident.error) {
        showToast(deletedIncident.error, 'error')
      }
    }
  }, [deletedIncident.loading])

  return isNew || (!isNew && fields.id !== 0) ? (
    <>
      <div className="sm:flex sm:items-start sm:justify-between">
        <div className="flex items-start gap-x-3">
          <h2 className="text-lg font-medium text-gray-800 dark:text-white">
            Siniestro TUS - Lesionados (
            {fields?.id !== 0 ? '#' + fields?.incident_number : 'nuevo'})
          </h2>
        </div>

        <div className="flex items-center mt-4 sm:mt-0 gap-x-4">
          <button
            title="Imprimir"
            type="button"
            className="flex items-center justify-center w-1/2 px-5 py-2 text-sm text-white capitalize transition-colors duration-200 border rounded-sm bg-accent border-accent sm:w-36 gap-x-2 enabled:hover:bg-white enabled:hover:text-accent dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:enabled:hover:bg-gray-800 disabled:opacity-30 focus:ring-opacity-20 focus:outline-none focus:ring focus:ring-accent focus:border-accent dark:focus:border-accent"
            onClick={() => {
              // Ajusta dinámicamente el tamaño del mapa antes y después de imprimir, ya que no se adapta correctamente a la pantalla de impresión.
              // Se redimensiona al iniciar la impresión y se restablece al finalizar.
              if (mapRef?.current) {
                const handleBeforePrint = (mapRef2) => {
                  mapRef2.current._container.classList.remove('w-full')
                  mapRef2.current._container.style.width = '800px'
                  mapRef2.current._container.style.height = '400px'
                  mapRef2.current.invalidateSize()
                }

                const handleAfterPrint = (mapRef2) => {
                  mapRef2.current._container.style.removeProperty('width')
                  mapRef2.current._container.classList.add('w-full')
                  mapRef2.current._container.style.height = '600px'
                  mapRef2.current.invalidateSize()
                }

                window.onbeforeprint = handleBeforePrint(mapRef)

                setTimeout(function () {
                  window.onafterprint = handleAfterPrint(mapRef)
                }, 10)
              }

              handlePrint()
            }}
          >
            <LuPrinter size={20} />
            <span>Imprimir</span>
          </button>

          <button
            title="Volver"
            type="button"
            className="flex items-center justify-center w-1/2 px-5 py-2 text-sm text-white capitalize transition-colors duration-200 border rounded-sm bg-accent border-accent sm:w-36 gap-x-2 enabled:hover:bg-white enabled:hover:text-accent dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:enabled:hover:bg-gray-800 disabled:opacity-30 focus:ring-opacity-20 focus:outline-none focus:ring focus:ring-accent focus:border-accent dark:focus:border-accent"
            onClick={() =>
              setLocation(
                (() => {
                  switch (params.view) {
                    case 'tus-incidents':
                      return '/tus-incidents/injured/grid'
                    case 'maps':
                      return '/maps/injured'
                    case 'maps-all':
                      return '/maps/all'
                    default:
                      return '/tus-incidents/injured/grid'
                  }
                })()
              )
            }
          >
            <LuMoveLeft size={20} />
            <span>Volver</span>
          </button>
        </div>
      </div>

      <InjuredFormDetail
        isNew={isNew}
        user={user}
        fields={fields}
        incidentRef={incidentRef}
        mapRef={mapRef}
        handleSubmit={handleSubmit}
        handleDelete={handleDelete}
      />
    </>
  ) : (
    <div className="flex items-center content-center justify-center w-full h-full">
      <p
        className="flex flex-col-reverse items-center justify-center h-full font-bold dark:text-[#F5F5F5] text-gray-700"
        title="Cargando..."
      >
        <ImSpinner9 size={70} className="animate-spin" />
      </p>
    </div>
  )
}

InjuredViewDetail.propTypes = {
  params: PropTypes.any
}
